import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class PlatformService {
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  get isClient(): boolean {
    return this.isBrowser;
  }

  get isServer(): boolean {
    return !this.isBrowser;
  }

  get window(): Window | null {
    return this.isBrowser ? window : null;
  }

  get document(): Document | null {
    return this.isBrowser ? document : null;
  }

  get localStorage(): Storage | null {
    return this.isBrowser ? localStorage : null;
  }

  get sessionStorage(): Storage | null {
    return this.isBrowser ? sessionStorage : null;
  }

  get navigator(): Navigator | null {
    return this.isBrowser ? navigator : null;
  }

  get location(): Location | null {
    return this.isBrowser ? location : null;
  }

  get matchMedia(): (query: string) => MediaQueryList | null {
    return this.isBrowser ? (query: string) => window.matchMedia(query) : () => null;
  }

  get innerWidth(): number {
    return this.isBrowser ? window.innerWidth : 0;
  }

  get innerHeight(): number {
    return this.isBrowser ? window.innerHeight : 0;
  }

  get pageYOffset(): number {
    return this.isBrowser ? window.pageYOffset : 0;
  }

  get documentElement(): HTMLElement | null {
    return this.isBrowser ? document.documentElement : null;
  }

  get body(): HTMLElement | null {
    return this.isBrowser ? document.body : null;
  }

  // Utility methods
  setLocalStorageItem(key: string, value: string): void {
    if (this.isBrowser && this.localStorage) {
      this.localStorage.setItem(key, value);
    }
  }

  getLocalStorageItem(key: string): string | null {
    if (this.isBrowser && this.localStorage) {
      return this.localStorage.getItem(key);
    }
    return null;
  }

  removeLocalStorageItem(key: string): void {
    if (this.isBrowser && this.localStorage) {
      this.localStorage.removeItem(key);
    }
  }

  setBodyDataAttribute(key: string, value: string): void {
    if (this.isBrowser && this.body) {
      this.body.dataset[key] = value;
    }
  }

  setDocumentElementStyle(property: string, value: string): void {
    if (this.isBrowser && this.documentElement) {
      this.documentElement.style.setProperty(property, value);
    }
  }

  addEventListener(type: string, listener: EventListener): void {
    if (this.isBrowser && this.window) {
      this.window.addEventListener(type, listener);
    }
  }

  removeEventListener(type: string, listener: EventListener): void {
    if (this.isBrowser && this.window) {
      this.window.removeEventListener(type, listener);
    }
  }

  setTimeout(callback: (...args: any[]) => void, delay: number): any {
    return this.isBrowser ? setTimeout(callback, delay) : 0;
  }

  setInterval(callback: (...args: any[]) => void, delay: number): any {
    return this.isBrowser ? setInterval(callback, delay) : 0;
  }

  clearTimeout(id: number): void {
    if (this.isBrowser) {
      clearTimeout(id);
    }
  }

  clearInterval(id: number): void {
    if (this.isBrowser) {
      clearInterval(id);
    }
  }

  createElement(tagName: string): HTMLElement | null {
    return this.isBrowser ? document.createElement(tagName) : null;
  }

  createElementNS(namespaceURI: string, qualifiedName: string): Element | null {
    return this.isBrowser ? document.createElementNS(namespaceURI, qualifiedName) : null;
  }

  atob(str: string): string {
    return this.isBrowser ? window.atob(str) : '';
  }

  btoa(str: string): string {
    return this.isBrowser ? window.btoa(str) : '';
  }

  reload(): void {
    if (this.isBrowser && this.location) {
      this.location.reload();
    }
  }
} 